import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  Image,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { secureFetch } from "../data/secureFetch";
import { BANNER_IMAGE_RECOMMENDATION } from "../utils/bannerImage";
import { processBannerImageFile } from "../hooks/useBannerImageUpload";

const blankForm = {
  id: null,
  title: "",
  url: "",
  description: "",
  status: "active",
  campaigns: [],
  bannerImageUrl: "",
  bannerImageDataUrl: "",
  bannerImageFileName: "",
  bannerImageWarning: "",
  removeBannerImage: false,
};

const toSnakeSlug = (value = "") =>
  (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

function FundGroups() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [form, setForm] = useState(blankForm);
  const handleCloseModal = () => {
    if (!saving) {
      onClose();
      setForm(blankForm);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsRes, campaignsRes] = await Promise.all([
        secureFetch("/api/fund-groups"),
        secureFetch("/api/fund-groups/campaign-options"),
      ]);

      if (!groupsRes.ok) {
        throw new Error(groupsRes.message || "Failed to load fund groups");
      }
      if (!campaignsRes.ok) {
        throw new Error(
          campaignsRes.message || "Failed to load active campaign options",
        );
      }

      const groupsData = Array.isArray(groupsRes?.data)
        ? groupsRes.data
        : Array.isArray(groupsRes)
          ? groupsRes
          : [];
      const campaignOptionsData = Array.isArray(campaignsRes?.data)
        ? campaignsRes.data
        : Array.isArray(campaignsRes)
          ? campaignsRes
          : [];

      setGroups(groupsData);
      setCampaignOptions(campaignOptionsData);
    } catch (error) {
      toast({
        title: "Load failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setForm(blankForm);
    onOpen();
  };

  const openEdit = async (groupId) => {
    try {
      const response = await secureFetch(`/api/fund-groups/${groupId}`);
      if (!response.ok) {
        throw new Error(response.message || "Failed to load fund group");
      }

      const next = {
        id: response.id,
        title: response.title || "",
        url: response.url || "",
        description: response.description || "",
        status: response.status || "active",
        campaigns: Array.isArray(response.campaigns)
          ? response.campaigns.map((c, idx) => ({
              campaignId: String(c.id),
              sortOrder:
                Number.isFinite(Number(c.sort_order)) && c.sort_order !== null
                  ? Number(c.sort_order)
                  : idx,
            }))
          : [],
        bannerImageUrl: response.banner_image_url || "",
        bannerImageDataUrl: "",
        bannerImageFileName: "",
        bannerImageWarning: "",
        removeBannerImage: false,
      };

      setForm(next);
      onOpen();
    } catch (error) {
      toast({
        title: "Load failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const removeGroup = async (groupId) => {
    try {
      const response = await secureFetch(`/api/fund-groups/${groupId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error(response.message || "Failed to delete fund group");
      }
      toast({
        title: "Fund group deleted",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      await loadData();
    } catch (error) {
      toast({
        title: "Delete failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const addCampaignRow = () => {
    setForm((prev) => ({
      ...prev,
      campaigns: [
        ...prev.campaigns,
        { campaignId: "", sortOrder: prev.campaigns.length },
      ],
    }));
  };

  const updateCampaignRow = (index, key, value) => {
    setForm((prev) => {
      const next = [...prev.campaigns];
      next[index] = {
        ...next[index],
        [key]: key === "sortOrder" ? Number(value || 0) : value,
      };
      return { ...prev, campaigns: next };
    });
  };

  const removeCampaignRow = (index) => {
    setForm((prev) => {
      const next = prev.campaigns.filter((_, i) => i !== index);
      return { ...prev, campaigns: next };
    });
  };

  const handleBannerImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setForm((prev) => ({
        ...prev,
        bannerImageDataUrl: "",
        bannerImageFileName: "",
        bannerImageWarning: "",
      }));
      return;
    }

    try {
      const prepared = await processBannerImageFile(file);
      setForm((prev) => ({
        ...prev,
        bannerImageDataUrl: prepared.dataUrl,
        bannerImageFileName: prepared.fileName,
        bannerImageWarning: prepared.warnings?.[0] || "",
        removeBannerImage: false,
      }));
    } catch (error) {
      event.target.value = "";
      setForm((prev) => ({
        ...prev,
        bannerImageDataUrl: "",
        bannerImageFileName: "",
        bannerImageWarning: "",
      }));
      toast({
        title: "Invalid Banner Image",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const saveForm = async () => {
    const title = form.title.trim();
    const url = form.url.trim();

    if (!title) {
      toast({
        title: "Title required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!url) {
      toast({
        title: "URL required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const normalizedCampaigns = [...form.campaigns]
      .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
      .map((row, idx) => ({
        campaignId: parseInt(row.campaignId, 10),
        sortOrder: Number.isFinite(Number(row.sortOrder))
          ? Number(row.sortOrder)
          : idx,
      }))
      .filter((row) => Number.isInteger(row.campaignId) && row.campaignId > 0);

    const duplicateCheck = new Set();
    for (const row of normalizedCampaigns) {
      if (duplicateCheck.has(row.campaignId)) {
        toast({
          title: "Duplicate campaign",
          description: "Each campaign can only be added once.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      duplicateCheck.add(row.campaignId);
    }

    setSaving(true);
    try {
      const payload = {
        title,
        url,
        description: form.description,
        status: form.status,
        campaigns: normalizedCampaigns,
      };

      const endpoint = form.id
        ? `/api/fund-groups/${form.id}`
        : "/api/fund-groups";
      const method = form.id ? "PUT" : "POST";

      const response = await secureFetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        data: payload,
      });

      if (!response.ok) {
        throw new Error(response.message || "Failed to save fund group");
      }
      const savedGroupId = response.id || form.id;
      let bannerWarning = "";

      if (form.bannerImageDataUrl && savedGroupId) {
        const bannerResponse = await secureFetch(
          `/api/fund-groups/${savedGroupId}/banner`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            data: { imageDataUrl: form.bannerImageDataUrl },
          },
        );
        if (!bannerResponse.ok) {
          bannerWarning =
            bannerResponse.message ||
            "Fund group saved, but banner upload failed.";
        }
      } else if (
        form.removeBannerImage &&
        form.bannerImageUrl &&
        savedGroupId
      ) {
        const removeResponse = await secureFetch(
          `/api/fund-groups/${savedGroupId}/banner`,
          {
            method: "DELETE",
          },
        );
        if (!removeResponse.ok) {
          bannerWarning =
            removeResponse.message ||
            "Fund group saved, but banner removal failed.";
        }
      }

      toast({
        title: form.id ? "Fund group updated" : "Fund group created",
        description: bannerWarning || undefined,
        status: bannerWarning ? "warning" : "success",
        duration: 3000,
        isClosable: true,
      });
      onClose();
      setForm(blankForm);
      await loadData();
    } catch (error) {
      toast({
        title: "Save failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={5}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Fund Groups</Heading>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreate}>
          Create Fund Group
        </Button>
      </Flex>

      <Text color="gray.600" mb={6}>
        Build custom donation URLs that let donors split one gift across
        multiple active campaigns.
      </Text>

      {loading ? (
        <Flex justify="center" py={12}>
          <Spinner size="lg" color="orange.400" />
        </Flex>
      ) : (
        <Stack spacing={4}>
          {groups.length === 0 && (
            <Box borderWidth="1px" borderRadius="md" p={6} bg="white">
              <Text color="gray.600">No fund groups yet.</Text>
            </Box>
          )}

          {groups.map((group) => (
            <Box
              key={group.id}
              borderWidth="1px"
              borderRadius="md"
              p={4}
              bg="white"
            >
              <Flex justify="space-between" align="start" gap={4}>
                <Box>
                  <Heading size="sm" mb={1}>
                    {group.title}
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    URL: {`/donate/${group.url}`}
                  </Text>
                  <HStack spacing={2}>
                    <Badge
                      colorScheme={group.status === "active" ? "green" : "gray"}
                    >
                      {group.status}
                    </Badge>
                    <Badge colorScheme="blue">
                      {group.campaign_count || 0} campaigns
                    </Badge>
                  </HStack>
                </Box>

                <HStack>
                  <IconButton
                    aria-label="Edit fund group"
                    icon={<EditIcon />}
                    onClick={() => openEdit(group.id)}
                  />
                  <IconButton
                    aria-label="Delete fund group"
                    icon={<DeleteIcon />}
                    colorScheme="red"
                    variant="outline"
                    onClick={() => removeGroup(group.id)}
                  />
                </HStack>
              </Flex>
            </Box>
          ))}
        </Stack>
      )}

      <Modal isOpen={isOpen} onClose={handleCloseModal} size="3xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {form.id ? "Edit Fund Group" : "Create Fund Group"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input
                  value={form.title}
                  onChange={(e) => {
                    const nextTitle = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      title: nextTitle,
                      url: prev.id ? prev.url : toSnakeSlug(nextTitle),
                    }));
                  }}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>URL</FormLabel>
                <Input
                  value={form.url}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      url: toSnakeSlug(e.target.value),
                    }))
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={form.status}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, status: e.target.value }))
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Banner Image (Optional)</FormLabel>
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleBannerImageChange}
                  p={1}
                />
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Recommended: {BANNER_IMAGE_RECOMMENDATION.recommendedWidth}x
                  {BANNER_IMAGE_RECOMMENDATION.recommendedHeight}px. Minimum:{" "}
                  {BANNER_IMAGE_RECOMMENDATION.minimumWidth}x
                  {BANNER_IMAGE_RECOMMENDATION.minimumHeight}px. Max{" "}
                  {BANNER_IMAGE_RECOMMENDATION.maxFileSizeMB}MB.
                </Text>
                {form.bannerImageFileName && (
                  <Text fontSize="sm" color="gray.700" mt={1}>
                    Selected: {form.bannerImageFileName}
                  </Text>
                )}
                {form.bannerImageWarning && (
                  <Text fontSize="sm" color="orange.500" mt={1}>
                    {form.bannerImageWarning}
                  </Text>
                )}
                {(form.bannerImageDataUrl ||
                  (form.bannerImageUrl && !form.removeBannerImage)) && (
                  <Box
                    mt={3}
                    borderWidth="1px"
                    borderRadius="md"
                    overflow="hidden"
                  >
                    <Image
                      src={form.bannerImageDataUrl || form.bannerImageUrl}
                      alt="Fund group banner preview"
                      width="100%"
                      maxH="160px"
                      objectFit="cover"
                    />
                  </Box>
                )}
                {form.bannerImageUrl && !form.bannerImageDataUrl && (
                  <Button
                    mt={2}
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        removeBannerImage: !prev.removeBannerImage,
                      }))
                    }
                  >
                    {form.removeBannerImage
                      ? "Keep Existing Banner"
                      : "Remove Existing Banner"}
                  </Button>
                )}
                {form.removeBannerImage && (
                  <Text mt={2} fontSize="sm" color="red.500">
                    Existing banner will be removed when you save.
                  </Text>
                )}
              </FormControl>

              <Box borderWidth="1px" borderRadius="md" p={4}>
                <Flex justify="space-between" align="center" mb={3}>
                  <Heading size="sm">Campaigns</Heading>
                  <Button
                    size="sm"
                    leftIcon={<AddIcon />}
                    onClick={addCampaignRow}
                  >
                    Add Campaign
                  </Button>
                </Flex>

                <Stack spacing={2}>
                  {form.campaigns.length === 0 && (
                    <Text fontSize="sm" color="gray.500">
                      Add one or more active campaigns and set their order.
                    </Text>
                  )}

                  {form.campaigns.map((row, idx) => (
                    <HStack key={`${row.campaignId}-${idx}`} align="start">
                      <FormControl>
                        <FormLabel fontSize="xs">Campaign</FormLabel>
                        <Select
                          value={row.campaignId}
                          onChange={(e) =>
                            updateCampaignRow(idx, "campaignId", e.target.value)
                          }
                        >
                          <option value="">Select campaign</option>
                          {campaignOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name}
                              {opt.goal > 0
                                ? ` (Goal: $${(opt.goal / 100).toFixed(2)})`
                                : ""}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl maxW="120px">
                        <FormLabel fontSize="xs">Order</FormLabel>
                        <Input
                          type="number"
                          value={row.sortOrder}
                          onChange={(e) =>
                            updateCampaignRow(idx, "sortOrder", e.target.value)
                          }
                        />
                      </FormControl>

                      <IconButton
                        aria-label="Remove campaign"
                        mt={6}
                        icon={<DeleteIcon />}
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => removeCampaignRow(idx)}
                      />
                    </HStack>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={handleCloseModal} isDisabled={saving}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={saveForm} isLoading={saving}>
              {form.id ? "Save Changes" : "Create Fund Group"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default FundGroups;
